import React, { useEffect, useState } from 'react';
import { Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar, Line,
} from 'recharts';
import { InventoryPage } from '../inventory/InventoryChrome';
import { Card, CardHeader, Kpi, Chip, Note } from '../../components/finance/financeUi';
import { money } from '../../components/finance/financeFormat';
import { RubyLoader } from '../../components/RubyLoader';
import { estateAdminApi, type EstateOverview } from '../../services/estateAdminApi';

/**
 * dataviz: ONE axis — fees and volume are both money, so they share one cents
 * scale rather than the dual-axis trap. Two hues, fixed meaning (never
 * cycled): ruby for RubyTill's own fee revenue (the thing this screen exists
 * to watch), slate for the gross rent volume it was earned on.
 */
const VOLUME_COLOR = '#64748B';
const FEE_COLOR = '#DC143C';

const currentMonth = (): string => new Date().toISOString().slice(0, 7);

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
}

const ConnectChip: React.FC<{ status: 'none' | 'onboarding' | 'ready' }> = ({ status }) => (
  <Chip tone={status === 'ready' ? 'emerald' : status === 'onboarding' ? 'amber' : 'stone'}>
    {status === 'ready' ? 'Ready' : status === 'onboarding' ? 'Onboarding' : 'Not connected'}
  </Chip>
);

const EstateOverviewPage: React.FC = () => {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState<EstateOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let off = false;
    setLoading(true);
    setError(null);
    estateAdminApi.overview(month)
      .then((r) => { if (!off) setData(r); })
      .catch((e) => { if (!off) setError(e?.message || 'Could not load the Ruby Estate overview'); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [month]);

  const chartData = (data?.trend || []).map((t) => ({
    month: monthLabel(t.month),
    volume: t.volumeCents / 100,
    fees: t.feeCents / 100,
  }));

  return (
    <InventoryPage icon={Building2} title="Ruby Estate" subtitle="Landlords, fees and Connect status across every Estate business" crumb="Ruby Estate">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-600"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="text-sm font-semibold text-stone-700 px-2 min-w-[90px] text-center">{monthLabel(month)}</span>
          <button
            type="button"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
            disabled={month >= currentMonth()}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-600 disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && <Note tone="danger">{error}</Note>}

      {loading || !data ? (
        <RubyLoader variant="page" label="Loading the Ruby Estate overview" />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Businesses" value={String(data.businesses)} />
            <Kpi label="Units" value={String(data.units)} sub={`${data.occupancyPct.toFixed(1)}% occupied`} />
            <Kpi label="Residents" value={String(data.residents)} sub={`${data.residentsWithLogin} signed in`} />
            <Kpi label="Active leases" value={String(data.activeLeases)} />
            <Kpi label="Rent processed" value={money(data.volumeCentsThisMonth)} sub="this month" />
            <Kpi label="RubyTill fees" value={money(data.feeCentsThisMonth)} sub={`${money(data.feeCentsYtd)} YTD`} />
            <Kpi label="Processing" value={String(data.processing)} tone={data.processing ? 'warn' : 'default'} />
            <Kpi label="Failed" value={String(data.failedThisMonth)} tone={data.failedThisMonth ? 'warn' : 'default'} sub="this month" />
            <Kpi label="Open work orders" value={String(data.openWorkOrders)} tone={data.openWorkOrders ? 'warn' : 'default'} />
          </div>

          <Card className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Connect status</span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="flex items-center gap-2 text-sm text-stone-700"><ConnectChip status="ready" /> {data.connect.ready} ready</span>
              <span className="flex items-center gap-2 text-sm text-stone-700"><ConnectChip status="onboarding" /> {data.connect.onboarding} onboarding</span>
              <span className="flex items-center gap-2 text-sm text-stone-700"><ConnectChip status="none" /> {data.connect.none} not connected</span>
            </div>
          </Card>

          <Card>
            <CardHeader title="Fees &amp; volume" hint="Last 12 months" />
            <div className="p-4" style={{ height: 300 }}>
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-stone-400">No data yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#E7E5E4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v: number) => `$${v.toFixed(2)}`} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="volume" name="Rent volume" fill={VOLUME_COLOR} radius={[4, 4, 0, 0]} maxBarSize={28} />
                    <Line dataKey="fees" name="RubyTill fees" stroke={FEE_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </Card>
        </div>
      )}
    </InventoryPage>
  );
};

export default EstateOverviewPage;
